import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchFacilities,
  cancelFetchFacilities,
  resetSearch,
} from "../../../redux/actions/facilityActions";
import { fetchPreviousHistory } from "../../../redux/actions/historyAction";
import SearchFields from "./SearchFields";
import Sorting from "./Sorting";
import SearchItem from "./SearchItem";
import Pagination from "./Pagination";
import ResultsMap from "./ResultsMapArcGISHooks";
import SearchItemPlaceholder from "../../shared/placeholders/SearchItemPlaceholder";
import ResultsMapPlaceholder from "../../shared/placeholders/ResultsMapPlaceholder";
import SearchGrid from "./SearchGrid";
import { useFormik } from "formik";
import urlParameterHelper from "../../../utilities/urlParameters";

export default function Search(props) {
  const params = urlParameterHelper.parse(props.location.search);
  const search = useSelector(state => state.search);
  const previousPage = useSelector(state => state.history.previousPage);
  const [queryParams, setQueryParams] = useState();
  const [showMap, setShowMap] = useState(true);
  const [viewMode, setViewMode] = useState("card");
  const dispatch = useDispatch();
  const isInitialMount = useRef(true);

  const formik = useFormik({
    initialValues: {
      filters: search.filters
    },
    onSubmit: values => {
      let f = values.filters;
      Object.keys(f).map((key, index) => {
        if (key.startsWith("multi")) {
          let object = f[key].map((c) => c.value).join(",");
          let name = key.substr(5).toLowerCase();
          f[name] = object;
        }

      });

      // I don't know why it's stringifying then parsing
      const qs = urlParameterHelper.stringify(f);
      const query = urlParameterHelper.parse(qs);
      const newQuery = `?${urlParameterHelper.stringify(query)}`;
      setQueryParams(newQuery);
      dispatch(fetchFacilities(newQuery, values.filters, search.searchResults));
      props.history.push(`/search?${qs}`);
    },
  })

  useEffect(() => {
    if (previousPage === "/landing") {
      resetFilters();
    }

    let filters = search.filters;
    for (let [key, value] of Object.entries(params)) {
      if (typeof value === "string" && value.toLowerCase() === "true") value = true;
      if (typeof value === "string" && value.toLowerCase() === "false") value = false;
      if (("page", "pageSize").includes(key)) value = parseInt(value);
      filters[key] = value;
    }
    filters.keyword = params.keyword !== undefined ? params.keyword : filters.keyword;
    formik.setFieldValue("filters", filters);

    if (previousPage === "/landing") {
      formik.handleSubmit();
    }
    dispatch(fetchPreviousHistory("/search"));
  }, [])

  const onViewModeChange = (e) => {
    e.preventDefault();
    if (viewMode === "grid") {
      setViewMode("card");
    } else {
      setViewMode("grid");
    }
  };

  const onSearchOptionsChange = (option) => {
    var key = Object.keys(option)[0];
    var value = option[key];

    let o = search.options;
    o[key] = value;
    formik.setFieldValue("options", o);
    formik.handleSubmit();
  };

  const onPageChange = (e, event) => {
    let f = search.filters;
    f.page = e.page;
    formik.setFieldValue("filters", f);
    formik.handleSubmit();
  };

  const onPageSizeChange = (e) => {
    let f = search.filters;
    f.page = 1;
    f[e.target.name] = e.target.value;
    formik.setFieldValue("filters", f);
    formik.handleSubmit();
  };

  const onSortingChange = (e) => {
    let f = search.filters;
    f[e.target.name] = e.target.value;
    formik.setFieldValue("filters", f);
    formik.handleSubmit();
  };

  const resetFilters = () => {
    formik.setFieldValue("filters", {
      keyword: "",
      epa_id: false,
      facility: false,
      street: "",
      city: "",
      active: "both",
      multicounty: [],
      zipCode: "",
      sort: "EPA_ID",
      sortBy: "ASC",
      addressType: "physical",
      manifestType: "all",
      epaIdCategory: "all",
      epaIdType: "all",
      type: "all",
      page: formik.values.page,
      pageSize: formik.values.pageSize,
      issueFDate: "",
      issueTDate: "",
      total_records: formik.values.total_records,
    });
    dispatch(resetSearch())
  };

  const getPageSize = () => {
    let arr = [];
    for (let i = 0; i < 10; i++) {
      arr.push(i);
    }
    return arr;
  };

  if (!search.searchResults) {
    return <SearchItemPlaceholder />;
  }

  let results = search.searchResults.map((result) => (
    <SearchItem key={result.EPA_ID} result={result} />
  ));

  const itemPlaceholder = getPageSize().map((page) => (
    <SearchItemPlaceholder key={page} />
  ));

  return (
    <div className="" style={{ height: "100%" }}>
      <SearchFields
        resetFilters={resetFilters}
        //resetFilters={() => dispatch(resetSearch())}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        isFetching={search.isFetching}
        cancelFetchFacilities={dispatch(cancelFetchFacilities)}
        formik={formik}
      />
      <div style={{ backgroundColor: "#F5F5F5" }}>
        {viewMode === "card" ? (
          <div className="searchcontainer">
            <div className="results-container">
              <div>
                <Sorting
                  onPageSizeChange={onPageSizeChange}
                  onSortingChange={onSortingChange}
                  onSearchOptionsChange={onSearchOptionsChange}
                  isFetching={search.isFetching}
                  filters={search.filters}
                  results={search.options}
                  formik={formik}
                />

                {search.isFetching ? (
                  <div>{itemPlaceholder}</div>
                ) : (
                    <div>{results}</div>
                  )}

                {search.searchResults.length > 0 ? (
                  <Pagination
                    onPageChange={onPageChange}
                    filters={search.filters}
                    results={search.options}
                  />
                ) : formik.submitCount === 0 ? (
                  <div
                    className="m-t-md"
                    style={{ fontStyle: "italic", textAlign: "center" }}
                  >
                    Search for Handlers using the search bar above.
                  </div>
                ) : null}
              </div>
            </div>
            {showMap && (
              <div className="half map-container">
                <div style={{ width: "100%", height: "100%" }}>
                  <ResultsMap
                    //linkToProfile={linkToProfile}
                    RESULTS={search.searchResults}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
            <div className="search-grid-container">
              <div className="search-grid-grid-container">
                <SearchGrid
                  initialPageSize={search.options.pageSize}
                  onPageSizeChange={onPageSizeChange}
                  onSortingChange={onSortingChange}
                  onPageChange={onPageChange}
                  resultCount={search.options.total_records}
                  queryParams={queryParams}
                  filters={search.filters}
                  results={search.options}
                />
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
